import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ShipmentWaybillOverview } from '../../models/shipment-waybill.model';
import { ShipmentWaybillMainInfoService } from './shipment-waybill-main-info.service';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { TransportMode } from '../../../../../common/constants/transport-mode';
import {
  ShipmentWaybillOverviewEdit,
  ShipmentWaybillOverviewLoad,
  ShipmentWaybillOverviewLoading,
  ShipmentWaybillOverviewSetInitialState,
} from './shipment-waybill-main-info.actions';
import { first } from 'lodash';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentWaybillOverviewStateModel {
  overview: ShipmentWaybillOverview;
  loaded: boolean;
}

const initialState: ShipmentWaybillOverviewStateModel = {
  overview: null,
  loaded: false,
};

@State<ShipmentWaybillOverviewStateModel>({
  name: 'shipmentWaybillOverview',
  defaults: initialState,
})
@Injectable()
export class ShipmentWaybillMainInfoState {
  constructor(
    private shipmentWaybillOverviewService: ShipmentWaybillMainInfoService,
    private notificationService: NotificationService,
  ) {}
  @Selector()
  static getState(state: ShipmentWaybillOverviewStateModel): ShipmentWaybillOverviewStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentWaybillOverviewStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getOverview(state: ShipmentWaybillOverviewStateModel): ShipmentWaybillOverview {
    return state?.overview ?? null;
  }

  @Selector()
  static getTransportMode(state: ShipmentWaybillOverviewStateModel): TransportMode {
    return !!state?.overview ? first(state.overview.serviceRequirementCodes) : TransportMode.Unknown;
  }

  @Action(ShipmentWaybillOverviewLoad)
  getWaybillOverview(
    ctx: StateContext<ShipmentWaybillOverviewStateModel>,
    { payload }: ShipmentWaybillOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillOverviewService.getOverviewData(payload).pipe(
      map((overview: ShipmentWaybillOverview) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillOverviewLoading)
  setIsLoading(ctx: StateContext<ShipmentWaybillOverviewStateModel>, action: ShipmentWaybillOverviewLoading): void {
    ctx.patchState({ loaded: action.payload });
  }

  @Action(ShipmentWaybillOverviewEdit)
  updateWaybillOverview(
    ctx: StateContext<ShipmentWaybillOverviewStateModel>,
    { payload }: ShipmentWaybillOverviewEdit,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillOverviewService.updateOverviewData(payload.id, payload.data).pipe(
      take(1),
      map(() => {
        const { overview } = ctx.getState();
        payload.dialogRef.close(null);
        this.notificationService.success('api.waybill.success.update');
        ctx.patchState({
          overview: {
            ...overview,
            commercialInvoiceNumbers: payload.data.invoices,
            deliveryNoteNumbers: payload.data.deliveryNotes,
            purchaseOrderNumbers: payload.data.purchaseOrders,
          },
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillOverviewSetInitialState)
  setInitialState(ctx: StateContext<ShipmentWaybillOverviewStateModel>): void {
    ctx.setState(initialState);
  }
}
